import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Intercom,
  show as intercomShow,
  hide as intercomHide,
  shutdown as intercomShutdown,
  update as intercomUpdate,
  onUnreadCountChange,
} from '@intercom/messenger-js-sdk';

const INTERCOM_APP_ID = import.meta.env.VITE_APP_INTERCOM_APP_ID;
const INTERCOM_REGION = import.meta.env.VITE_APP_INTERCOM_REGION;

export type IntercomHook = {
  /** Whether the Intercom widget is ready and openable */
  isAvailable: boolean;

  /** Current unread message count from Intercom */
  unreadCount: number;

  /** Open the Intercom messenger. Returns false if Intercom is not actually available. */
  show: () => boolean;

  /** Hide the Intercom messenger */
  hide: () => void;

  /** Update Intercom with user data or settings */
  update: (data: Record<string, unknown>) => void;

  /** Shutdown Intercom session (call on logout) */
  shutdown: () => void;
};

export const useInternalIntercom = (shouldBoot: boolean): IntercomHook => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const hasBooted = useRef(false);
  const readyCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Boot Intercom when shouldBoot becomes true
  useEffect(() => {
    if (hasBooted.current || !shouldBoot) {
      return;
    }

    try {
      Intercom({
        app_id: INTERCOM_APP_ID,
        region: INTERCOM_REGION,
        hide_default_launcher: true,
        alignment: 'right',
        horizontal_padding: 20,
        vertical_padding: 20,
      });

      hasBooted.current = true;

      // Confirm the SDK is actually available on the page before marking ready
      readyCheckRef.current = setInterval(() => {
        if (typeof (window as unknown as Record<string, unknown>).Intercom === 'function') {
          if (readyCheckRef.current) {
            clearInterval(readyCheckRef.current);
          }

          setIsAvailable(true);
        }
      }, 100);

      // Give up polling after 5s (don't force unavailable — if already set, keep it)
      const timeout = setTimeout(() => {
        if (readyCheckRef.current) {
          clearInterval(readyCheckRef.current);
        }
      }, 5000);

      return () => {
        if (readyCheckRef.current) {
          clearInterval(readyCheckRef.current);
        }

        clearTimeout(timeout);
      };
    } catch {
      // Intercom failed to boot (e.g. blocked by ad blocker)
      setIsAvailable(false);
    }
  }, [shouldBoot]);

  // Register unread count listener after boot
  useEffect(() => {
    if (!isAvailable) {
      return;
    }

    onUnreadCountChange((count: number) => {
      setUnreadCount(count);
    });
  }, [isAvailable]);

  const show = useCallback((): boolean => {
    if (!isAvailable || typeof (window as unknown as Record<string, unknown>).Intercom !== 'function') {
      return false;
    }

    try {
      intercomShow();
      return true;
    } catch {
      return false;
    }
  }, [isAvailable]);

  const hide = useCallback(() => {
    if (!isAvailable) {
      return;
    }

    intercomHide();
  }, [isAvailable]);

  const update = useCallback(
    (data: Record<string, unknown>) => {
      if (!isAvailable) {
        return;
      }

      intercomUpdate(data);
    },
    [isAvailable],
  );

  const shutdown = useCallback(() => {
    if (!isAvailable) {
      return;
    }

    intercomShutdown();
    hasBooted.current = false;
    setIsAvailable(false);
    setUnreadCount(0);
  }, [isAvailable]);

  return { isAvailable, unreadCount, show, hide, update, shutdown };
};
